import { DEFAULT_BOOLEAN_FEEDBACK } from '../constants';

export type CategoryItem = {
  category: string;
  description: string;
};

export type CategoricalMetadata = {
  categories: CategoryItem[];
};

export type ContinuousMetadata = {
  min: number;
  max: number;
  minDescription: string;
  maxDescription: string;
};

export type FeedbackMetadata =
  | {
      type: 'categorical';
      categoricalConfig: CategoricalMetadata;
    }
  | {
      type: 'continuous';
      continuousConfig: ContinuousMetadata;
    }
  | {
      type: 'boolean';
    };

// Types for serialized schema
export interface SchemaProperty {
  type: string;
  description?: string;
  enum?: string[];
  minimum?: number;
  maximum?: number;
}

export type EvaluatedEntityType =
  | 'dataset-eval'
  | 'session-eval'
  | 'thread-eval';
export interface SchemaDefinition {
  title?: string;
  description?: string;
  type: string;
  properties: Record<string, SchemaProperty>;
  required?: string[];
}

/**
 * Serializes the UI feedback configuration into a JSON schema format
 */
export function serializeFeedbackToSchema(
  name: string,
  description: string,
  metadata: FeedbackMetadata,
  includeReasoning: boolean
): SchemaDefinition {
  // Initialize the schema structure
  const schema: SchemaDefinition = {
    title: 'extract',
    description: "Extract information from the user's response.",
    type: 'object',
    properties: {},
    required: [name],
  };

  // Create the property for this feedback
  const property: SchemaProperty = {
    type: 'string',
    description: description,
  };

  // Handle different feedback types
  if (metadata.type === 'categorical') {
    property.type = 'string';

    // Add enum values from categories
    property.enum = metadata.categoricalConfig.categories.map(
      (cat) => cat.category
    );

    // Append category descriptions to the main description if they exist
    const categoryDescriptions = metadata.categoricalConfig.categories
      .filter((cat) => cat.description)
      .map((cat) => `"${cat.category}": ${cat.description}`)
      .join('| ');

    if (categoryDescriptions) {
      property.description = `${
        property.description || ''
      } Categories: ${categoryDescriptions}`;
    }
  } else if (metadata.type === 'continuous') {
    property.type = 'number';
    property.minimum = metadata.continuousConfig.min;
    property.maximum = metadata.continuousConfig.max;

    // Append min/max descriptions if they exist
    const minMaxDescriptions: string[] = [];
    if (metadata.continuousConfig.minDescription) {
      minMaxDescriptions.push(
        `Min (${metadata.continuousConfig.min}): ${metadata.continuousConfig.minDescription}`
      );
    }
    if (metadata.continuousConfig.maxDescription) {
      minMaxDescriptions.push(
        `Max (${metadata.continuousConfig.max}): ${metadata.continuousConfig.maxDescription}`
      );
    }

    if (minMaxDescriptions.length > 0) {
      property.description = `${
        property.description || ''
      } ${minMaxDescriptions.join('| ')}`;
    }
  } else if (metadata.type === 'boolean') {
    property.type = 'boolean';
  }

  // Add the property to the schema
  schema.properties[name] = property;

  if (includeReasoning) {
    schema.properties['comment'] = {
      type: 'string',
      description: 'Reasoning for the score',
    };
  }
  return schema;
}

/**
 * Deserializes a JSON schema into UI feedback configuration
 */
export function deserializeSchemaToFeedback(schema: SchemaDefinition | null): {
  name: string;
  description: string;
  metadata: FeedbackMetadata;
  includeReasoning: boolean;
} | null {
  // If no schema or no properties, return null
  if (
    !schema ||
    !schema.properties ||
    Object.keys(schema.properties).length === 0
  ) {
    return null;
  }

  // Get the first property (assuming one feedback per schema for now)
  const name = Object.keys(schema.properties).filter(
    (key) => key !== 'comment'
  )[0];
  const property = schema.properties[name];

  // Extract the base description (without category/min-max details)
  let description = property.description || '';

  // Initialize metadata with default values
  let metadata: FeedbackMetadata;

  if (property.type === 'string' && property.enum) {
    // Handle categorical feedback
    const categories: CategoryItem[] = [];

    // Extract category descriptions if they exist
    const categoryDescRegex = /"([^"]+)":\s*([^|]+)/g;
    const categoryMatches = [...description.matchAll(categoryDescRegex)];

    // Create a map of category to description
    const categoryDescMap = new Map<string, string>();
    categoryMatches.forEach((match) => {
      categoryDescMap.set(match[1], match[2]);
    });

    // Clean up the description by removing category descriptions
    if (categoryMatches.length > 0) {
      description = description.split('Categories:')[0].trim();
    }

    // Create category items
    property.enum.forEach((category) => {
      categories.push({
        category,
        description: categoryDescMap.get(category) || '',
      });
    });

    metadata = {
      type: 'categorical',
      categoricalConfig: {
        categories:
          categories.length > 0
            ? categories
            : [{ category: 'category', description: '' }],
      },
    };
  } else if (property.type === 'number' || property.type === 'integer') {
    // Handle continuous feedback
    let minDescription = '';
    let maxDescription = '';

    // Extract min/max descriptions if they exist
    const minDescRegex = /Min\s*\((\d+)\):\s*([^|]+)/;
    const maxDescRegex = /Max\s*\((\d+)\):\s*([^|]+)/;

    const minMatch = description.match(minDescRegex);
    const maxMatch = description.match(maxDescRegex);

    if (minMatch) {
      minDescription = minMatch[2];
    }

    if (maxMatch) {
      maxDescription = maxMatch[2];
    }

    // Clean up the description by removing min/max descriptions
    if (minMatch || maxMatch) {
      description = description
        .split(/Min\s*\(\d+\):|Max\s*\(\d+\):/)[0]
        .trim();
    }

    metadata = {
      type: 'continuous',
      continuousConfig: {
        min: property.minimum !== undefined ? property.minimum : 1,
        max: property.maximum !== undefined ? property.maximum : 10,
        minDescription,
        maxDescription,
      },
    };
  } else if (property.type === 'boolean') {
    metadata = {
      type: 'boolean',
    };
  } else {
    // Default to boolean if type is not recognized
    metadata = DEFAULT_BOOLEAN_FEEDBACK;
  }

  const includeReasoning = Object.keys(schema.properties).includes('comment');
  return {
    name,
    description,
    metadata,
    includeReasoning,
  };
}
